import React, { Component } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5map from "@amcharts/amcharts5/map";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5geodata_worldLow from "@amcharts/amcharts5-geodata/worldLow";
import am5geodata_indiaHigh from "@amcharts/amcharts5-geodata/indiaHigh";

function cssColorToAm5Color(cssColor) {
	const ctx = document.createElement("canvas").getContext("2d");
	if (!ctx) return am5.color(0xff0000);
	ctx.fillStyle = cssColor;
	return am5.color(ctx.fillStyle);
}

class WorldMap extends Component {
	constructor(props) {
		super(props);
		this.chartRef = React.createRef();
		this.root = null;
		this.state = { mapFailed: false };
	}

	componentDidMount() {
		try {
			this.createChart();
		} catch (err) {
			console.error("WorldMap: failed to render map", err);
			this.setState({ mapFailed: true });
		}
	}

	componentDidUpdate(prevProps) {
		if (this.state.mapFailed) return;
		if (prevProps.data !== this.props.data || prevProps.color !== this.props.color) {
			try {
				this.setSeriesData();
			} catch (err) {
				console.error("WorldMap: failed to update map data", err);
				this.setState({ mapFailed: true });
			}
		}
	}

	componentWillUnmount() {
		if (this.root) {
			this.root.dispose();
		}
	}

	buildValueLookup() {
		const lookup = {};
		(this.props.data || []).forEach((entry) => {
			if (entry && typeof entry.country === "string") {
				lookup[entry.country.toUpperCase()] = entry.value;
			}
		});
		return lookup;
	}

	stylePolygonTemplate(template, tooltipName) {
		const color = cssColorToAm5Color(this.props.color || "red");
		const label = tooltipName || "{name}";

		template.setAll({
			fill: am5.color(0xdddddd),
			fillOpacity: 0,
			stroke: am5.color(0x000000),
			strokeWidth: 1,
			strokeOpacity: 0.2,
		});

		template.adapters.add("fill", (fill, target) => {
			const dataItem = target.dataItem;
			return dataItem && dataItem.get("value") !== undefined ? color : fill;
		});

		template.adapters.add("fillOpacity", (fillOpacity, target) => {
			const dataItem = target.dataItem;
			return dataItem && dataItem.get("value") !== undefined ? 1 : fillOpacity;
		});

		template.adapters.add("tooltipText", (text, target) => {
			const dataItem = target.dataItem;
			return dataItem && dataItem.get("value") !== undefined ? `${label}: {value}` : label;
		});
	}

	createChart() {
		if (this.root) {
			this.root.dispose();
		}

		this.root = am5.Root.new(this.chartRef.current);
		this.root.setThemes([am5themes_Animated.new(this.root)]);
		if (this.root._logo) this.root._logo.dispose();

		const chart = this.root.container.children.push(
			am5map.MapChart.new(this.root, {
				panX: "translateX",
				panY: "translateY",
				projection: am5map.geoMercator(),
			})
		);

		this.worldSeries = chart.series.push(
			am5map.MapPolygonSeries.new(this.root, {
				geoJSON: am5geodata_worldLow,
				exclude: ["IN", "AQ"],
				valueField: "value",
			})
		);
		this.stylePolygonTemplate(this.worldSeries.mapPolygons.template);

		this.indiaSeries = chart.series.push(
			am5map.MapPolygonSeries.new(this.root, {
				geoJSON: am5geodata_indiaHigh,
				valueField: "value",
			})
		);
		this.stylePolygonTemplate(this.indiaSeries.mapPolygons.template, "India");

		this.setSeriesData();
	}

	setSeriesData() {
		if (!this.worldSeries || !this.indiaSeries) return;

		const valueByCountry = this.buildValueLookup();
		const indiaValue = valueByCountry["IN"];

		this.worldSeries.data.setAll(
			Object.keys(valueByCountry).map((country) => ({
				id: country,
				value: valueByCountry[country],
			}))
		);

		this.indiaSeries.data.setAll(
			am5geodata_indiaHigh.features.map((feature) => {
				const data = { id: feature.properties.id };
				if (indiaValue !== undefined) data.value = indiaValue;
				return data;
			})
		);
	}

	render() {
		if (this.state.mapFailed) {
			return (
				<div
					style={{
						width: "100%",
						height: "300px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						color: "#999",
					}}
				>
					Unable to load map
				</div>
			);
		}

		return (
			<div
				ref={this.chartRef}
				style={{ width: "100%", height: "300px" }}
			></div>
		);
	}
}

export default WorldMap;
